import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PropertyCard } from "@/components/PropertyCard";
import { InquiryForm } from "@/components/InquiryForm";
import { usePublishedListings } from "@/data/useListings";

export default function FurnishedPrivateHomes() {
  const homes = usePublishedListings("furnished_private_home");
  const loading = homes === null;
  const list = homes || [];

  const scrollToHomes = () => {
    const el = document.getElementById("available-homes");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="private-homes-page" className="pt-28 md:pt-32">
      {/* HERO */}
      <section className="px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">
              Furnished Private Homes
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.02] text-[#1C1917]">
              Furnished Private Homes
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-4 text-lg md:text-xl text-[#8A9A86] font-light tracking-tight">Your Home Away From Home</p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light max-w-3xl">
              Experience the comfort and privacy of an entire furnished home in the Austin area. Our thoughtfully
              furnished private homes are ideal for families, relocating professionals, corporate assignments, and
              guests seeking comfortable extended stays.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              {list.length > 0 ? (
                <button
                  onClick={scrollToHomes}
                  data-testid="browse-available-homes"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8A9A86] text-white px-8 py-3.5 hover:bg-[#77876F] transition-colors duration-300 font-medium text-sm"
                >
                  Browse Available Homes <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </button>
              ) : (
                <Link
                  to="/book"
                  data-testid="browse-available-homes"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8A9A86] text-white px-8 py-3.5 hover:bg-[#77876F] transition-colors duration-300 font-medium text-sm"
                >
                  Browse Available Homes <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* AVAILABLE HOMES */}
      <section id="available-homes" className="px-6 md:px-12 pb-24 md:pb-32 mt-16 scroll-mt-28">
        <div className="max-w-7xl mx-auto">
          {!loading && list.length > 0 && (
            <>
              <Reveal>
                <h2 className="text-base md:text-lg font-medium tracking-tight text-[#1C1917]">Available private homes</h2>
              </Reveal>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="private-homes-listings">
                {list.map((u, i) => <PropertyCard key={u.slug} property={u} index={i} />)}
              </div>
            </>
          )}

          {!loading && list.length === 0 && (
            <Reveal>
              <div data-testid="private-homes-coming-soon">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#F3F6F2] text-[#4A6A48] px-4 py-1.5 text-xs font-medium tracking-[0.12em] uppercase">
                  <Clock className="w-3.5 h-3.5" strokeWidth={2} /> Coming Soon
                </div>
                <h2 className="mt-5 text-2xl md:text-3xl font-light tracking-tight text-[#1C1917] max-w-2xl">
                  Entire furnished homes are on their way.
                </h2>
                <p className="mt-3 text-base text-[#44403C] font-light leading-relaxed max-w-2xl">
                  We're preparing a collection of whole-home rentals you can book directly. Here's what to expect:
                </p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl">
                  {["Entire, fully furnished single-family homes", "Ideal for families, relocations & corporate stays", "Full kitchen, in-home laundry, Wi-Fi & parking", "Private, quiet and move-in ready"].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-[#44403C]"><Check className="w-4 h-4 text-[#8A9A86] mt-0.5 shrink-0" strokeWidth={2} /> {t}</li>
                  ))}
                </ul>
                <div className="mt-10"><InquiryForm category="furnished_private_home" categoryLabel="Furnished Private Homes" /></div>
                <Link to="/properties" data-testid="private-homes-view-furnished" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#8A9A86] hover:text-[#1C1917] transition-colors">
                  Browse our available Furnished Units <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
